import { FadeUp, SectionHeading } from "./Reveal";

const E = "/projects/emerson";

const PhaseLabel = ({ num, title }) => (
  <div className="flex items-center gap-4 mb-6">
    <span className="font-display text-4xl md:text-5xl font-bold text-[#4361C2]">{num}</span>
    <span className="text-xs tracking-[0.3em] uppercase text-ash border-b border-white/15 pb-1">{title}</span>
  </div>
);

const Caption = ({ children }) => (
  <div className="mt-3 text-[10px] tracking-[0.25em] uppercase text-ash">{children}</div>
);

const Img = ({ src, alt, testId, className = "" }) => (
  <img
    src={src}
    alt={alt}
    loading="lazy"
    className={`w-full object-cover border border-white/15 grayscale hover:grayscale-0 transition-all duration-700 ${className}`}
    data-testid={testId}
  />
);

const FACTS = [
  ["Location", "663 Emerson St NE, Washington DC"],
  ["Scope", "Row House Addition + Renovation"],
  ["Architect", "With Jlofton Studio"],
  ["Status", "In Progress"],
];

export const CaseStudyEmerson = () => (
  <section id="case-study-emerson" className="px-6 md:px-12 py-28 md:py-40 bg-surface border-y border-white/10" data-testid="emerson-case-study-section">
    <SectionHeading index="Case Study 03 — Residential Renovation" title="Emerson St. House" testId="emerson-case-study-heading" />

    <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-8 mb-20 md:mb-28">
      <FadeUp className="md:col-span-5">
        <dl className="border border-white/15 divide-y divide-white/10" data-testid="emerson-case-study-facts">
          {FACTS.map(([k, v]) => (
            <div key={k} className="flex items-center justify-between gap-4 px-5 py-4">
              <dt className="text-[10px] tracking-[0.3em] uppercase text-ash shrink-0">{k}</dt>
              <dd className="text-sm font-medium text-right">{v}</dd>
            </div>
          ))}
        </dl>
      </FadeUp>
      <FadeUp delay={0.1} className="md:col-span-6 md:col-start-7">
        <p className="text-lg md:text-xl font-light leading-relaxed text-paper" data-testid="emerson-case-study-intro">
          A classic DC brick row house in Northeast, getting a full rear addition. In collaboration
          with Jlofton Studio, ETMB is expanding the home upward and outward — new second-story
          volume off the back, tied carefully into the existing masonry shell.
        </p>
        <p className="mt-5 text-sm leading-relaxed text-ash">
          Row house work is surgical: tight lot lines, party walls, and an existing structure that
          has to keep standing while everything changes behind it. Construction is underway now.
        </p>
      </FadeUp>
    </div>

    <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8 mb-20 md:mb-28">
      <FadeUp className="md:col-span-12">
        <PhaseLabel num="01" title="The Row House" />
      </FadeUp>
      <FadeUp className="md:col-span-5">
        <Img src={`${E}/front-photo.jpg`} alt="663 Emerson St NE — existing brick row house front" testId="emerson-front-image" className="aspect-[4/5]" />
        <Caption>The existing row house — Emerson St NE</Caption>
      </FadeUp>
      <FadeUp delay={0.1} className="md:col-span-7 md:mt-24">
        <Img src={`${E}/side-photo.jpg`} alt="Side elevation of the existing brick row house" testId="emerson-side-image" className="aspect-[4/3]" />
        <Caption>The brick shell — side elevation before work</Caption>
      </FadeUp>
      <FadeUp className="md:col-span-7 md:col-start-6">
        <Img src={`${E}/rear-photo.jpg`} alt="Rear of the row house before the addition" testId="emerson-rear-before-image" className="aspect-[4/3]" />
        <Caption>The rear before — a small shed volume to be replaced</Caption>
      </FadeUp>
    </div>

    <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8 mb-20 md:mb-28">
      <FadeUp className="md:col-span-12">
        <PhaseLabel num="02" title="The Drawings" />
      </FadeUp>
      <FadeUp className="md:col-span-8">
        <div className="border border-white/15 overflow-hidden bg-[#0d1530]">
          <img src={`${E}/drawings.jpg`} alt="Permit drawing set for the Emerson St addition" loading="lazy" className="w-full invert opacity-80 mix-blend-screen" data-testid="emerson-drawings-image" />
        </div>
        <Caption>Full permit set — plans, elevations, sections — with Jlofton Studio</Caption>
      </FadeUp>
      <FadeUp delay={0.1} className="md:col-span-4 flex items-center">
        <blockquote className="border-l-2 border-[#4361C2] pl-6 py-2" data-testid="emerson-case-study-quote">
          <p className="font-display text-xl md:text-2xl uppercase font-bold leading-tight tracking-tight">
            "Tight lot. Party walls. Zero room for guesswork."
          </p>
        </blockquote>
      </FadeUp>
    </div>

    <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8">
      <FadeUp className="md:col-span-12">
        <PhaseLabel num="03" title="In Progress" />
      </FadeUp>
      <FadeUp className="md:col-span-7">
        <Img src={`${E}/progress-wide.jpg`} alt="Rear addition framed and wrapped, new windows installed" testId="emerson-progress-wide-image" className="aspect-[4/3]" />
        <Caption>The new rear volume — framed, wrapped, windows in</Caption>
      </FadeUp>
      <FadeUp delay={0.1} className="md:col-span-5 md:mt-24">
        <Img src={`${E}/progress-rear.jpg`} alt="Rear addition seen from the alley" testId="emerson-progress-rear-image" className="aspect-[4/5]" />
        <Caption>From the alley — second-story addition taking shape</Caption>
      </FadeUp>
    </div>
  </section>
);
